import type { SupabaseClient } from '../client'
import type { AiSummary } from '../domain/ai-summary'
import { unwrapList, unwrapMaybe } from './_helpers'

const SUMMARY_COLUMNS =
  'id, project_id, initiative_id, sprint_id, summary_type, title, content, urgency, progress_pct, model_used, source_data, generated_at, expires_at'

/**
 * Resumo IA com `content` parseado de JSON string (formato gravado no banco).
 */
export type AiSummaryParsed = Omit<AiSummary, 'content'> & {
  content: string
  parsed: unknown
}

function parseSummary(row: AiSummary): AiSummaryParsed {
  let parsed: unknown = null
  if (row.content) {
    try {
      parsed = JSON.parse(row.content)
    } catch {
      parsed = row.content
    }
  }
  return { ...row, parsed }
}

export type SummaryFilter = {
  initiativeId?: number | null
}

/**
 * Busca o resumo mais recente (não expirado) de um projeto por `summary_type`.
 * Se `opts.initiativeId` for fornecido, filtra também por iniciativa.
 */
export async function getLatestSummary(
  client: SupabaseClient,
  projectId: number,
  summaryType: string = 'sprint_progress',
  opts: SummaryFilter = {},
): Promise<AiSummaryParsed | null> {
  let query = client
    .schema('portal')
    .from('ai_summaries')
    .select(SUMMARY_COLUMNS)
    .eq('project_id', projectId)
    .eq('summary_type', summaryType)
    .or('expires_at.is.null,expires_at.gt.' + new Date().toISOString())
    .order('generated_at', { ascending: false })
    .limit(1)

  if (typeof opts.initiativeId === 'number') {
    query = query.eq('initiative_id', opts.initiativeId)
  } else if (opts.initiativeId === null) {
    query = query.is('initiative_id', null)
  }

  const result = await query.maybeSingle()
  const row = unwrapMaybe(result) as unknown as AiSummary | null
  return row ? parseSummary(row) : null
}

/**
 * Lista todos os resumos de um tipo para um projeto, mais recentes primeiro.
 * Usado em histórico/timeline. Aceita filtro opcional por iniciativa.
 */
export async function listSummariesByProject(
  client: SupabaseClient,
  projectId: number,
  summaryType: string = 'sprint_progress',
  opts: SummaryFilter = {},
): Promise<AiSummaryParsed[]> {
  let query = client
    .schema('portal')
    .from('ai_summaries')
    .select(SUMMARY_COLUMNS)
    .eq('project_id', projectId)
    .eq('summary_type', summaryType)
    .order('generated_at', { ascending: false })

  if (typeof opts.initiativeId === 'number') {
    query = query.eq('initiative_id', opts.initiativeId)
  } else if (opts.initiativeId === null) {
    query = query.is('initiative_id', null)
  }

  const rows = unwrapList(await query) as unknown as AiSummary[]
  return rows.map(parseSummary)
}

export const getLatestSprintProgress = (
  client: SupabaseClient,
  projectId: number,
  opts: SummaryFilter = {},
) => getLatestSummary(client, projectId, 'sprint_progress', opts)
